import * as React from 'react';
import colors from '../../../config/theme/colors';
import { Container, Item } from 'Components/Grid';
import CircularProgressLocal from 'Components/CircularProgress';

const typeMap = {
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
};

const returnFileSize = (sizeBytes: number) => {
  if (sizeBytes < 1e3) {
    return `${sizeBytes} bytes`;
  } else if (sizeBytes >= 1e3 && sizeBytes < 1e6) {
    return `${(sizeBytes / 1e3).toFixed(1)} KB`;
  } else {
    return `${(sizeBytes / 1e6).toFixed(1)} MB`;
  }
};

export const AttachmentForm = ({
  fm,
  attachmentData,
  handleFileChange,
  allowedTypes = ['.jpg', '.jpeg', '.png', '.pdf'],
  attachmentLimit,
}: IAttachmentForm) => {
  const [fileUploadError, setFileUploadError] = React.useState<'type' | 'size' | null>(null);
  const [uploadingFile, setUploadingFile] = React.useState<boolean>(false);

  const onFileOnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileUploadError(null);
    setUploadingFile(false);
    const file = e.target.files?.[0]; // Files, but the input currently only supports one file (no multiple prop). Iterating over the Filelist might require some refactoring to the whole file handleFileChange handler

    // Check that the file is one of the allowed file types.
    // This only ensures that the user did not select "All files" from the native file selection dialog and chose some other file type.
    const isAllowedFileType = allowedTypes.some(at => file?.type === typeMap[at]);
    // Here you could also check the file size limit
    if (isAllowedFileType) {
      handleFileChange(e);
    } else {
      setFileUploadError('type');
    }
  };

  // Event listener for file upload dialog cancel action
  addEventListener('cancel', event => {
    setUploadingFile(false);
  });

  return (
    <div style={{ display: 'grid', justifyItems: 'center', maxWidth: '40rem', overflow: 'hidden' }}>
      {fileUploadError && (
        <div style={{ color: colors.error.default, marginBottom: '2rem', fontSize: '1.4rem' }}>
          {fileUploadError === 'type' ? fm('general.fileWrongType') : <></>}
        </div>
      )}

      {attachmentLimit && attachmentData.length >= attachmentLimit ? (
        <></>
      ) : (
        <div style={{ color: colors.primary, textAlign: 'center', maxWidth: '40rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <label htmlFor="image_upload" className="button-sq filled" onClick={() => setUploadingFile(true)}>
              {uploadingFile ? (
                <CircularProgressLocal
                  style={{
                    display: 'block',
                    color: colors.white,
                  }}
                  size={'2rem'}
                />
              ) : (
                fm('messaging.file.choose')
              )}
            </label>
          </div>
          <input
            id="image_upload"
            name="image_upload"
            type="file"
            onChange={onFileOnChangeHandler}
            style={{ opacity: 0, height: 0 }}
            accept={allowedTypes.join(', ')}
          />
          <div style={{ fontSize: '1.4rem', color: colors.darkGray, marginTop: '-2rem' }}>
            {fm('messaging.file.allowedTypes')}: {allowedTypes.join(', ')}
          </div>
        </div>
      )}
      {attachmentData.length > 0 && (
        <>
          <div style={{ fontWeight: 600, marginTop: '1rem', fontSize: '1.6rem' }}>{fm('messaging.file.chosen')}:</div>
          {attachmentData.map((att, i) => (
            <Container justifyContent="space-around" key={i} style={{ fontSize: '1.4rem', fontWeight: '400' }}>
              <Item xs={6} style={{ wordBreak: 'break-all' }}>
                {att.name}
              </Item>
              <Item xs={true} style={{ textAlign: 'center' }}>
                {att.type}
              </Item>
              <Item xs={true} style={{ textAlign: 'center' }}>
                {returnFileSize(att.size)}
              </Item>
            </Container>
          ))}
        </>
      )}
    </div>
  );
};

interface IAttachmentForm {
  fm: (id: string) => string;
  attachmentData: File[];
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  allowedTypes?: Array<'.jpg' | '.jpeg' | '.png' | '.pdf'>;
  /** How many attachments can be selected at one time */
  attachmentLimit?: number;
}
